import { Component } from '@theme/component';

/** @typedef {{ scroller: HTMLElement, prevButton: HTMLButtonElement, nextButton: HTMLButtonElement, arrowsContainer: HTMLElement }} Refs */

/** @extends {Component<Refs>} */
export class NewProductsListComponent extends Component {
    #scrollAmount = 349;
    #abortController = new AbortController();
    /** @type {number | undefined} */
    #resizeRaf;
    /** @type {number | undefined} */
    #scrollRaf;

    connectedCallback() {
        super.connectedCallback();
        this.#init();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    updatedCallback() {
        this.#abortController.abort();
        this.#abortController = new AbortController();
        this.#init();
    }

    #init() {
        const { signal } = this.#abortController;

        this.#updateGutter();

        // The initial snap runs against the CSS calc() fallback (which the snap
        // engine ignores), landing on the wrong offset. Re-trigger it once now
        // that the px gutter is applied so the first card aligns to the content.
        if (this.classList.contains('new-products-list--full-bleed')) {
            requestAnimationFrame(() => {
                if (this.refs.scroller) this.refs.scroller.scrollLeft = 0;
            });
        }

        this.#updateArrows();

        this.refs.scroller?.addEventListener('scroll', this.#handleScroll, { signal, passive: true });
        window.addEventListener('resize', this.#handleResize, { signal, passive: true });
    }

    #handleResize = () => {
        if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
        this.#resizeRaf = requestAnimationFrame(() => {
            this.#updateGutter();
            this.#updateArrows();
        });
    };

    #handleScroll = () => {
        if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
        this.#scrollRaf = requestAnimationFrame(() => this.#updateArrows());
    };

    /**
     * Resolve the content-column gutter to a px value so scroll-snap honors the
     * first-card alignment — the snap engine ignores calc() percentages in
     * scroll-padding, but resolves a px-valued custom property.
     */
    #updateGutter() {
        const scroller = this.refs.scroller;
        if (!scroller) return;

        if (!this.classList.contains('new-products-list--full-bleed')) {
            scroller.style.removeProperty('--npl-gutter');
            return;
        }

        const reference = this.querySelector('.new-products-list__header') ?? this;
        const gutter = Math.max(0, Math.round(reference.getBoundingClientRect().left));
        scroller.style.setProperty('--npl-gutter', `${gutter}px`);
    }

    /**
     * Hide the arrows when every card already fits, and flag the prev/next
     * buttons as disabled at the start/end of the scroll track.
     */
    #updateArrows() {
        const { scroller, prevButton, nextButton, arrowsContainer } = this.refs;
        if (!scroller || !arrowsContainer) return;

        const hasOverflow = scroller.scrollWidth - scroller.clientWidth > 1;
        arrowsContainer.hidden = !hasOverflow;
        if (!hasOverflow) return;

        const atStart = scroller.scrollLeft <= 1;
        const atEnd = Math.ceil(scroller.scrollLeft + scroller.clientWidth) >= scroller.scrollWidth - 1;

        this.#setDisabled(prevButton, atStart);
        this.#setDisabled(nextButton, atEnd);
    }

    /**
     * @param {HTMLButtonElement | undefined} button
     * @param {boolean} disabled
     */
    #setDisabled(button, disabled) {
        if (!button) return;
        button.setAttribute('aria-disabled', String(disabled));
    }

    #scrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    scrollPrev() {
        if (this.refs.prevButton?.getAttribute('aria-disabled') === 'true') return;
        this.refs.scroller?.scrollBy({ left: -this.#scrollAmount, behavior: this.#scrollBehavior() });
    }

    scrollNext() {
        if (this.refs.nextButton?.getAttribute('aria-disabled') === 'true') return;
        this.refs.scroller?.scrollBy({ left: this.#scrollAmount, behavior: this.#scrollBehavior() });
    }
}

if (!customElements.get('new-products-list-component')) {
    customElements.define('new-products-list-component', NewProductsListComponent);
}
